/**
 * The text field the filter bars and forms are built from (`.m-input` in the
 * prototype's modals.css).
 *
 * An eyebrow label over a 36px control with an optional leading glyph. Focus
 * moves the border to the accent and adds a 3px accent-soft ring — the same
 * treatment the select uses, which is why both read as one family.
 *
 * > **The stylesheet's numbers, not the ticket's.** CAE-1539 describes "14px
 * > text, 0.5px `--line` border, padding `9px 12px`, 2px ring"; `.m-input-control`
 * > is 13.5px on a `--line-strong` border at a fixed 36px height with a 3px ring.
 * > The heavier border matters: an input has to read as an input against a white
 * > card, and `--line` is the divider weight. Logged in
 * > docs/design/admin-ui/prototype/README.md.
 *
 * Built on a plain `<input>` rather than MUI's `TextField`, which renders its
 * own label, fieldset and notch — three elements to override before the 0.5px
 * border is even reachable.
 */
import Box from '@mui/material/Box';
import { useId, type ChangeEvent, type ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';

export interface AdminInputProps {
  /** Always required — with `hideLabel` it becomes the field's accessible name. */
  label: string;
  hideLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * `email` and `password` were added by CAE-1556, which put the two auth
   * screens on this control. They are not cosmetic: `password` is what masks
   * the field and tells a password manager which one holds the credential, and
   * `email` is what a phone shows the @ keyboard for.
   */
  type?: 'text' | 'search' | 'date' | 'email' | 'password';
  /** A leading glyph inside the control, e.g. `<SearchIcon />`. */
  icon?: ReactNode;
  disabled?: boolean;
  /**
   * The browser's autofill hint — `email`, `current-password`, `new-password`.
   *
   * Worth passing wherever it applies: without it a password manager cannot
   * tell the three fields on the change-password screen apart, and will
   * cheerfully fill the old password into all of them.
   */
  autoComplete?: string;
  /** Submitted name, for a form a browser might remember. */
  name?: string;
  required?: boolean;
  /**
   * What is wrong with the value, shown under the control and announced.
   *
   * A string rather than a boolean: a field that is merely *red* tells an
   * operator something is wrong and not what, and the screens that needed this
   * (CAE-1556's auth and user forms) all had a sentence to say.
   */
  error?: string;
  onBlur?: () => void;
  /**
   * Asks LastPass and 1Password to leave this field alone.
   *
   * `autoComplete="off"` is advisory and the managers routinely ignore it, so
   * a plain text field whose label happens to read like a credential gets a
   * vault popup over it. The Asset Library's **Name** is the case this exists
   * for — it carried these three attributes before CAE-1556's review moved it
   * onto this control, and dropping them would hand back the annoyance
   * somebody had already fixed.
   */
  ignorePasswordManagers?: boolean;
  /**
   * A `data-testid` on the control itself.
   *
   * Here because the e2e suite drives the login form by id — a Playwright spec
   * cannot reach a React prop, and CAE-1556's rebuild of that screen onto this
   * component dropped the three ids it had and broke the spec. Prefer a role or
   * label query where you can; this is for the cases already written.
   */
  testId?: string;
}

/** Shared with the select so the two cannot drift apart. */
export const controlSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '36px',
  minWidth: 0,
  padding: '0 12px',
  borderRadius: `${theme.tokens.radius.control}px`,
  border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
  backgroundColor: theme.tokens.color.surface,
  transition: 'border-color 0.12s, box-shadow 0.12s',
  '&:focus-within': {
    borderColor: theme.tokens.color.accent,
    boxShadow: `0 0 0 3px ${theme.tokens.color.accentSoft}`,
  },
});

export const fieldLabelSx = (theme: Theme) => ({
  fontSize: theme.tokens.text.eyebrow.fontSize,
  fontWeight: theme.tokens.text.eyebrow.fontWeight,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: theme.tokens.color.ink4,
});

export const AdminInput = ({
  label,
  hideLabel,
  value,
  onChange,
  placeholder,
  type = 'text',
  icon,
  disabled,
  autoComplete,
  name,
  required,
  error,
  onBlur,
  ignorePasswordManagers,
  testId,
}: AdminInputProps) => {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
      {hideLabel ? null : (
        <Box component="label" htmlFor={id} sx={fieldLabelSx}>
          {label}
        </Box>
      )}
      <Box
        sx={[
          controlSx,
          // The error edge replaces the resting one but not the focus one: a
          // field being corrected is still the field with focus in it, and
          // losing the accent ring mid-edit reads as the control going dead.
          ...(error
            ? [
                (theme: Theme) => ({
                  borderColor: theme.tokens.color.danger,
                  '&:focus-within': {
                    borderColor: theme.tokens.color.danger,
                    boxShadow: `0 0 0 3px ${theme.tokens.color.dangerSoft}`,
                  },
                }),
              ]
            : []),
        ]}
      >
        {icon ? (
          <Box
            aria-hidden
            sx={(theme: Theme) => ({
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              fontSize: '14px',
              color: theme.tokens.color.ink4,
              // `MuiSvgIcon-root` sets its own `font-size: 1.5rem`, so a glyph
              // handed in as `icon={<SearchIcon />}` ignores the 14px above and
              // paints at 24 inside a 36px control. Every other primitive sizes
              // the icon at its call site; this slot takes whatever it is given,
              // so it sizes it here instead.
              '& svg': { fontSize: 'inherit' },
            })}
          >
            {icon}
          </Box>
        ) : null}
        <Box
          component="input"
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          name={name}
          required={required}
          data-testid={testId}
          {...(ignorePasswordManagers
            ? { 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-form-type': 'other' }
            : {})}
          aria-label={hideLabel ? label : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          onBlur={onBlur}
          sx={(theme: Theme) => ({
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontFamily: theme.tokens.font.sans,
            fontSize: '13.5px',
            color: theme.tokens.color.ink,
            fontVariantNumeric: 'tabular-nums',
            '&::placeholder': { color: theme.tokens.color.ink4 },
            // The native picker glyph is the design's calendar affordance; the
            // prototype only softens it rather than replacing it.
            '&::-webkit-calendar-picker-indicator': { opacity: 0.55, cursor: 'pointer' },
          })}
        />
      </Box>
      {error ? (
        <Box
          id={errorId}
          /*
           * Not `role="alert"`. The field already points at this through
           * `aria-describedby`, so a screen reader reads it when focus lands
           * on the control it is about; an alert would also interrupt to read
           * it out of context, and a form with three invalid fields would
           * announce three sentences about nothing in particular.
           */
          sx={(theme: Theme) => ({
            fontSize: '12px',
            color: theme.tokens.color.danger,
          })}
        >
          {error}
        </Box>
      ) : null}
    </Box>
  );
};
